import type { KubernetesConfigMapProperties, KubernetesServerSideApplyProperties } from "@octopusdeploy/legacy-action-properties";
import { ActionExecutionLocation } from "@octopusdeploy/octopus-server-client";
import * as _ from "lodash";
import * as React from "react";
import { TargetRoles } from "~/areas/projects/components/Process/types";
import type { ActionSummaryProps } from "~/components/Actions/actionSummaryProps";
import { KubernetesConfigMapComponent } from "~/components/Actions/kubernetes/kubernetesConfigMapComponent";
import { kubernetesMixedExecutionLocationConfig } from "~/components/Actions/kubernetes/kubernetesMixedExecutionLocationConfig";
import KubernetesStatusCheckComponent, { InitialStatusCheckProperties } from "~/components/Actions/kubernetes/kubernetesStatusCheckComponent";
import type { ActionEditProps } from "~/components/Actions/pluginRegistry";
import pluginRegistry from "~/components/Actions/pluginRegistry";
import type { ScriptPackageProperties } from "~/components/Actions/script/scriptPackageReference";
import { BaseComponent } from "~/components/BaseComponent/BaseComponent";
import StringKeyValueEditList from "~/components/EditList/KeyValueEditList";
import { FormSectionHeading } from "~/components/form";
import { default as ExpandableFormSection } from "~/components/form/Sections/ExpandableFormSection";
import Summary from "~/components/form/Sections/Summary";
import { toBoolString } from "~/utils/fieldConverters";
import { JsonUtils } from "~/utils/jsonUtils";
import { getKubernetesTargetDiscoveryCloudProviders } from "./getKubernetesTargetDiscoveryCloudProviders";
import KubernetesNamespaceFormSection from "./kubernetesNamespaceFormSection";
import { ServerSideApplyFormSection } from "./serverSideApplyFormSection";
class KubernetesDeployConfigMapActionSummary extends BaseComponent<ActionSummaryProps, never> {
    constructor(props: ActionSummaryProps) {
        super(props);
    }
    render() {
        return <div>Deploy a config map resource to Kubernetes</div>;
    }
    static displayName = "KubernetesDeployConfigMapActionSummary";
}
export interface KubernetesDeployConfigMapActionProperties extends KubernetesConfigMapProperties, KubernetesServerSideApplyProperties {
}
type KubernetesDeployConfigMapActionEditProps = ActionEditProps<KubernetesDeployConfigMapActionProperties, ScriptPackageProperties>;
class KubernetesDeployConfigMapActionEdit extends BaseComponent<KubernetesDeployConfigMapActionEditProps, never> {
    constructor(props: ActionEditProps<KubernetesDeployConfigMapActionProperties, ScriptPackageProperties>) {
        super(props);
    }
    async componentDidMount() {
        if (this.props.properties["Octopus.Action.Kubernetes.ServerSideApply.Enabled"] === undefined) {
            this.setDefaultServerSideApply();
        }
    }
    private setDefaultServerSideApply() {
        this.props.setProperties({ ["Octopus.Action.Kubernetes.ServerSideApply.Enabled"]: "True" });
        this.props.setProperties({ ["Octopus.Action.Kubernetes.ServerSideApply.ForceConflicts"]: "True" });
    }
    render() {
        return (<div>
                <KubernetesStatusCheckComponent jobsSupported={false} timeoutSupported={false} statusCheckSupported={true} showLegacyWait={false} properties={this.props.properties} getFieldError={this.props.getFieldError} setProperties={this.props.setProperties} localNames={this.props.localNames} expandedByDefault={this.props.expandedByDefault}/>
                <KubernetesConfigMapComponent actionInputsDependencies={this.props.actionInputsDependencies} properties={this.props.properties} packages={this.props.packages} plugin={this.props.plugin} getFieldError={this.props.getFieldError} setProperties={this.props.setProperties} setPackages={this.props.setPackages} doBusyTask={this.props.doBusyTask} busy={this.props.busy} errors={this.props.errors} projectId={this.props.projectId} gitRef={this.props.gitRef} expandedByDefault={this.props.expandedByDefault} importLabels={true} localNames={this.props.localNames}/>
                <ExpandableFormSection errorKey="Octopus.Action.KubernetesContainers.DeploymentLabels" isExpandedByDefault={this.props.expandedByDefault} title="Config Map Labels" summary={this.labelsSummary()} help={"The labels applied to the config map resource"}>
                    <StringKeyValueEditList items={this.props.properties["Octopus.Action.KubernetesContainers.DeploymentLabels"]} name="Label" separator="" onChange={(val) => this.props.setProperties({ ["Octopus.Action.KubernetesContainers.DeploymentLabels"]: val })} valueLabel="Value" keyLabel="Name" hideBindOnKey={false} projectId={this.props.projectId} gitRef={this.props.gitRef} localNames={this.props.localNames}/>
                </ExpandableFormSection>
                <KubernetesNamespaceFormSection namespace={this.props.properties["Octopus.Action.KubernetesContainers.Namespace"]} onChange={(ns) => this.props.setProperties({ ["Octopus.Action.KubernetesContainers.Namespace"]: ns })} localNames={this.props.localNames}/>

                <FormSectionHeading title="Additional Configuration Options"/>
                <ServerSideApplyFormSection enabled={this.props.properties["Octopus.Action.Kubernetes.ServerSideApply.Enabled"] === "True"} forceConflicts={this.props.properties["Octopus.Action.Kubernetes.ServerSideApply.ForceConflicts"] === "True"} onEnabledChange={(value) => this.props.setProperties({ ["Octopus.Action.Kubernetes.ServerSideApply.Enabled"]: toBoolString(value) })} onForceConflictsChange={(value) => this.props.setProperties({ ["Octopus.Action.Kubernetes.ServerSideApply.ForceConflicts"]: toBoolString(value) })}></ServerSideApplyFormSection>
            </div>);
    }
    private labelsSummary() {
        const labels = _.toPairs(JsonUtils.tryParse<Record<string, string>>(this.props.properties["Octopus.Action.KubernetesContainers.DeploymentLabels"], {}));
        if (labels.length === 0) {
            return Summary.placeholder("The config map resource labels");
        }
        return Summary.summary(<span>
                Add the label{labels.length > 1 && <span>s</span>}{" "}
                {_.chain(labels)
                .flatMap((pair) => [
                <strong key={pair[0]}>
                            {pair[0]}: {pair[1]}
                        </strong>,
                <span key={`${pair[0]}_,`}>, </span>,
            ])
                .slice(0, -1)
                .value()}
            </span>);
    }
    static displayName = "KubernetesDeployConfigMapActionEdit";
}
pluginRegistry.registerAction({
    executionLocation: ActionExecutionLocation.AlwaysOnServer,
    actionType: "Octopus.KubernetesDeployConfigMap",
    summary: (properties, targetRolesAsCSV) => <KubernetesDeployConfigMapActionSummary properties={properties} targetRolesAsCSV={targetRolesAsCSV}/>,
    editSections: {
        top: (props: KubernetesDeployConfigMapActionEditProps) => <KubernetesDeployConfigMapActionEdit {...props}/>,
        default: (_: KubernetesDeployConfigMapActionEditProps) => <></>,
    },
    canHaveChildren: (step) => true,
    canBeChild: true,
    targetRoleOption: (action) => TargetRoles.Required,
    hasPackages: (action) => false,
    targetDiscoveryCloudConnectionProviders: getKubernetesTargetDiscoveryCloudProviders,
    getInitialProperties: () => InitialStatusCheckProperties,
    canUseExecutionTimeouts: false,
    mixedExecutionLocations: kubernetesMixedExecutionLocationConfig,
    disableInlineExecutionContainers: true,
});
